import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { isRole, Roles } from "../utils/constants/user";
import { BaseUrl } from "../utils/constants/serverName";
import { FEATURE } from "../utils/constants/permission";

type RouteRecordAuthorized = {
  meta: {
    navigation: boolean;
    icon?: string;
    category?: string;
    rolesAllowed: Roles[];
    featureNames?: FEATURE[];
  };
  children?: RouteRecordAuthorized[];
};

export type RouteRecordExtended = RouteRecordRaw & RouteRecordAuthorized;

const routes: Array<RouteRecordExtended> = [
  {
    path: "/llm/:caseId?",
    alias: "/",
    name: "Large Language Model",
    component: () => import("../components/LLM.vue"),
    meta: {
      rolesAllowed: ["PUBLIC", "ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED", "ROLE_SPECIALIST"],
      navigation: true,
      icon: "PartitionOutlined",
    },
  },

  {
    // New start-page clickdummy ("KI-Tools & Funktionen"). Standalone route for
    // now; the "/" alias stays on /llm until this is promoted.
    path: "/home",
    name: "Home Catalog",
    component: () => import("../components/Home/HomeCatalog.vue"),
    meta: {
      rolesAllowed: ["PUBLIC", "ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_SPECIALIST"],
      navigation: false,
      icon: "HomeOutlined",
    },
  },

  {
    // Frontends for the tools microservice suite (served under ${ServerName}/tools).
    path: "/tools/pdf-split",
    name: "Tool PDF Split",
    component: () => import("../components/tools/PdfSplitTool.vue"),
    meta: {
      rolesAllowed: ["PUBLIC", "ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_SPECIALIST"],
      navigation: false,
      icon: "ToolOutlined",
    },
  },
  {
    path: "/tools/images-to-pdf",
    name: "Tool Images To PDF",
    component: () => import("../components/tools/ImagesToPdfTool.vue"),
    meta: {
      rolesAllowed: ["PUBLIC", "ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_SPECIALIST"],
      navigation: false,
      icon: "ToolOutlined",
    },
  },

  {
    path: "/access-denied",
    name: "navigation.accessdenied",
    component: () => import("../components/Landing/AccessDenied.vue"),
    meta: {
      navigation: false,
      rolesAllowed: ["PUBLIC", "ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED", "ROLE_SPECIALIST"],
    },
  },

  {
    path: "/customer",
    name: "navigation.customer",
    component: () => import("../components/Customer.vue"),
    meta: {
      navigation: false,
      rolesAllowed: ["PUBLIC", "ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED", "ROLE_SPECIALIST"],
    },
  },

  {
    path: "/documentation",
    name: "documentation",
    component: () => import("../components/Documentation.vue"),
    meta: {
      navigation: false,
      rolesAllowed: ["ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED", "ROLE_SPECIALIST"],
    },
  },

  {
    path: "/settings",
    name: "Settings",
    components: {
      default: () => import("../components/Settings.vue"),
      helper: () => import("../components/DocumentList.vue"),
    },
    children: [
      {
        path: "/settings/password",
        alias: "/changePassword",
        name: "navigation.password",
        component: () => import("../components/settings/PasswordChange.vue"),
        meta: {
          category: "settings.general",
          navigation: false,
          rolesAllowed: ["PUBLIC", "ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED", "ROLE_SPECIALIST"],
        },
      },

      {
        path: "/settings/tenant/:tenantId?",
        name: "navigation.tenant",
        component: () => import("../components/settings/Tenant.vue"),
        meta: {
          category: "settings.general",
          navigation: false,
          rolesAllowed: ["ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED"],
        },
        props: true,
      },
      {
        path: "/settings/tenants",
        name: "navigation.tenants",
        component: () => import("../components/settings/Tenants.vue"),
        meta: {
          category: "settings.general",
          navigation: false,
          rolesAllowed: ["ROLE_ADMIN"],
        },
      },
      {
        path: "/settings/system",
        name: "navigation.system",
        component: () => import("../components/settings/SystemStatus.vue"),
        meta: {
          category: "settings.general",
          navigation: false,
          rolesAllowed: ["ROLE_ADMIN"],
        },
      },
      {
        path: "/settings/broadcasts",
        name: "settings.broadcasts",
        component: () => import("../components/settings/Broadcasts.vue"),
        meta: {
          category: "settings.general",
          navigation: false,
          rolesAllowed: ["ROLE_ADMIN"],
        },
      },
      {
        path: "/settings/audit-log",
        name: "settings.auditLog",
        component: () => import("../components/settings/AuditLog.vue"),
        meta: {
          category: "settings.general",
          navigation: false,
          rolesAllowed: ["ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED"],
        },
      },
      {
        path: "/settings/users",
        name: "navigation.users",
        component: () => import("../components/settings/Users.vue"),
        meta: {
          category: "settings.general",
          navigation: false,
          rolesAllowed: ["ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED"],
        },
      },
      {
        path: "/settings/models",
        name: "settings.models",
        component: () => import("../components/settings/Models.vue"),
        meta: {
          rolesAllowed: ["ROLE_ADMIN"],
          navigation: false,
          category: "settings.documentanalysis",
        },
        props: true,
      },
      {
        path: "/settings/websitechecksettings/:tenantId?",
        name: "navigation.websitechecksettings.report",
        component: () => import("../components/settings/WebsiteCheckSettings.vue"),
        meta: {
          category: "settings.websitechecksettings",
          navigation: false,
          rolesAllowed: ["ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED"],
        },
      },
    ],
    meta: {
      rolesAllowed: ["ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED", "ROLE_SPECIALIST"],
      navigation: false,
    },
  },

  {
    path: "/text-analyse",
    redirect: (to) => {
      return {
        path: `/document/${to.query.docid}`,
      };
    },
    meta: {
      rolesAllowed: ["ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED", "ROLE_SPECIALIST"],
      navigation: false,
    },
  },

  {
    path: "/document/:docId",
    alias: "/ocr/:docId",
    name: "DocumentViewer",
    component: () => import("../components/DocumentViewer.vue"),
    meta: {
      rolesAllowed: ["ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED", "ROLE_SPECIALIST"],
      navigation: false,
    },
    props: true,
  },

  {
    path: "/case/:Id",
    name: "CaseViewer",
    component: () => import("../components/cases/CaseViewer.vue"),
    meta: {
      rolesAllowed: ["ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED", "ROLE_SPECIALIST"],
      navigation: false,
      featureNames: ["case-list"],
    },
    props: true,
  },

  {
    path: "/websitecheck/:Id",
    name: "WebsiteCheck",
    component: () => import("../components/websitecheck/details/WebsiteCheckViewer.vue"),
    meta: {
      rolesAllowed: ["ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED", "ROLE_SPECIALIST"],
      navigation: false,
      featureNames: ["website-check-standard", "website-check-swiss", "website-check-swiss-2", "website-check-security", "website-check-premium", "case-list"],
    },
    props: true,
  },

  {
    path: "/document",
    name: "navigation.documents",
    component: () => import("../components/DocumentList.vue"),
    meta: {
      rolesAllowed: ["ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED", "ROLE_SPECIALIST"],
      navigation: true,
      icon: "SearchOutlined",
      featureNames: ["doc-analysis"],
    },
  },

  {
    path: "/cases",
    name: "navigation.cases",
    component: () => import("../components/CaseList.vue"),
    meta: {
      rolesAllowed: ["ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED", "ROLE_SPECIALIST"],
      navigation: true,
      icon: "FolderOpenOutlined",
      featureNames: ["case-list"],
    },
  },

  {
    path: "/websitecheck",
    name: "navigation.websitecheck",
    component: () => import("../components/websitecheck/WebsiteCheckList.vue"),
    meta: {
      rolesAllowed: ["ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED", "ROLE_SPECIALIST"],
      navigation: true,
      icon: "CheckCircleOutlined",
      featureNames: ["website-check-standard", "website-check-swiss", "website-check-swiss-2", "website-check-security", "website-check-premium"],
    },
  },

  {
    path: "/netzbeweis",
    name: "NetzBeweis",
    component: () => import("../components/Netzbeweis.vue"),
    meta: {
      rolesAllowed: ["ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED", "ROLE_SPECIALIST"],
      navigation: true,
      icon: "NetzbeweisIcon",
      featureNames: ["netzbeweis"],
    },
  },

  {
    path: "/llm/workflows/:id?",
    name: "Workflows",
    component: () => import("../components/Workflow.vue"),
    meta: {
      rolesAllowed: ["ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED", "ROLE_SPECIALIST"],
      navigation: false,
      icon: "",
    },
  },

  {
    path: "/llm/playbooks/:id?",
    name: "Playbooks",
    component: () => import("../components/Playbooks.vue"),
    meta: {
      rolesAllowed: ["ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED", "ROLE_SPECIALIST"],
      navigation: false,
      icon: "",
    },
  },

  {
    path: "/calculator",
    name: "Rechner",
    component: () => import("../components/calculators/Calculator.vue"),
    meta: {
      rolesAllowed: ["ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED", "ROLE_SPECIALIST"],
      navigation: true,
      icon: "CalculatorOutlined",
      featureNames: ["calculator"],
    },
  },

  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("../components/Landing/NotFound.vue"),
    meta: {
      navigation: false,
      rolesAllowed: ["PUBLIC", "ROLE_ADMIN", "ROLE_TENANTADMIN", "ROLE_TENANTADMIN_RESTRICTED", "ROLE_SPECIALIST"],
    },
  },
];

const mainRouter = createRouter({
  history: createWebHistory(BaseUrl),
  routes,
});

export default mainRouter;

export function getNavigationRoutes(userRole: Roles | string, enabledFeatures: FEATURE[], isOAuthToken: boolean) {
  const role = isRole(userRole) ? userRole : "PUBLIC";

  let navigationRoutes = routes.filter((route) => route.meta.navigation === true);

  for (const route of navigationRoutes) {
    if (isOAuthToken && route.name === "navigation.password") {
      navigationRoutes = navigationRoutes.filter((p) => p.name !== route.name);
      continue;
    }

    if (!route.meta.rolesAllowed.includes(role)) {
      navigationRoutes = navigationRoutes.filter((p) => p.name !== route.name);
    }

    if (route.meta.featureNames) {
      if (!route.meta.featureNames.some((item) => enabledFeatures.includes(item))) {
        navigationRoutes = navigationRoutes.filter((p) => p.name !== route.name);
      }
    }
  }

  return navigationRoutes;
}

export const getSettings = (userRole: Roles | string, isOAuthToken: boolean) => {
  const role = isRole(userRole) ? userRole : "PUBLIC";
  const settingsPages = [] as {
    name: string;
    routes: { name: string; path: string }[];
  }[];
  const settingsPage = routes.find((route) => route.path === "/settings");
  if (settingsPage != null) {
    if (settingsPage.children) {
      for (const child of settingsPage.children) {
        if (isOAuthToken && child.name === "navigation.password") {
          continue;
        }

        if (child.name === "stats") {
          continue;
        }
        if (!child.meta.rolesAllowed.includes(role)) {
          continue;
        }

        const categoryName = child.meta.category as string | "";
        const existingItem = settingsPages.find((p) => p.name === categoryName);
        if (existingItem != null) {
          existingItem.routes.push({
            name: child.name as string,
            path: child.path,
          });
        } else {
          settingsPages.push({
            name: categoryName,
            routes: [{ name: child.name as string, path: child.path }],
          });
        }
      }
    }
  }
  return settingsPages;
};
